/**
 * What the assistant can be asked for, offered as suggestions above the composer. One group,
 * "Suggestions". A suggestion the tools cannot answer comes back as a refusal, which is worse than
 * not offering it, so a new one is tried against the server before it goes in here.
 *
 * Chosen from what people actually ask, not from what the reports can sum: the prod MCP log for
 * 2026-10-02..07 (1,631 calls from 13 people, support account left out) had company revenue
 * reports in under 1% of the calls. The questions most people asked were when trainings run and
 * how many seats are sold, their own won deals, and which courses cover a topic — so those lead,
 * with the company totals behind them. First of all comes the one thing no report can do: explain
 * an error the user just hit, which testers on dev-1 asked for more than any figure.
 *
 * `icon` is a feather name: the kit's Button renders those, so a row needs no icon mechanism
 * of its own.
 *
 * The shell draws a heading whenever a row's `group` differs from the row above it, so every row
 * carries the same group to keep it to one heading.
 */
const assistantCommands = [
    { group: "Suggestions", icon: "alert-circle", label: "Explain the error I just got" },
    { group: "Suggestions", icon: "calendar", label: "Trainings in the next 30 days, seats sold" },
    { group: "Suggestions", icon: "award", label: "What deals have I won this year?" },
    { group: "Suggestions", icon: "briefcase", label: "Top 5 accounts by revenue this year" },
    { group: "Suggestions", icon: "book-open", label: "What cybersecurity courses do we offer?" },
];

/**
 * What "More" adds to the five, in the CRM window only — the extension's panel is too narrow for
 * more than the five. No `group`: the list they are drawn in has no headings.
 *
 * Every suggestion here and above was asked of the assistant on dev-1 on 2026-10-08. Two needed
 * the backend: "deals I won" listed the whole company's until the note named the signed-in user,
 * and "vs last year" gave only the change until the prompt asked for both periods' figures.
 * "Sellers" rather than "salespeople", and "booked": asked about salespeople, the model reached for
 * sales_report — which has a sales-people filter but no ranking — one time in three. The
 * Riyadh question needs list_campaigns' startsFrom/startsTo, which reach prod with
 * feat/assistant-readonly.
 *
 * Left out on purpose until the tools change: trainings delivered in a period (the model guesses
 * campaign statuses and types that do not exist — Delivered, Active, "Public" — and gets nothing
 * back), progress against target (target_attainment answers 0% with no target company-wide), and
 * best-rated trainings (training_quality ranks a 5.0 from two surveys first), and top campaigns by
 * revenue (answered from list_campaigns' won revenue on some turns and sales_breakdown's booked on
 * others — two different top fives, the fifth row changing between runs). Dropped for want of
 * anyone asking: revenue by region, deals won last month, win rate.
 */
export const moreCommands = [
    { icon: "dollar-sign", label: "Booked, paid and collected sales this year" },
    { icon: "repeat", label: "Sales this year vs the same period last year" },
    { icon: "activity", label: "Quarterly sales trend, last three years" },
    { icon: "grid", label: "Revenue by campaign type this year" },
    { icon: "layers", label: "Revenue by industry this year" },
    { icon: "users", label: "Top 5 sellers by booked revenue this year" },
    { icon: "map-pin", label: "Riyadh trainings in the next 60 days" },
    { icon: "check-square", label: "What are my overdue tasks?" },
];

export default assistantCommands;
